import { useMemo } from 'react';

import useAIJobChannel from 'hooks/useAIJobChannel';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import AIJob from 'models/AIJob';
import Admission from 'models/reviews/Admission';
import { ADMISSION_CHANNEL } from 'services/api/constants';

type SingleJobSubscriberProps = {
  jobId: string;
  onComplete: () => void;
};

function SingleJobSubscriber({ jobId, onComplete }: SingleJobSubscriberProps) {
  useAIJobChannel({
    params: { ai_job_id: jobId },
    onMessageReceived: (data) => {
      if (!new AIJob(data).isProcessing) onComplete();
    },
  });

  return null;
}

type ReviewSubscriberProps = {
  admissionId: string;
  onReceived: () => void;
};

function ReviewSubscriber({ admissionId, onReceived }: ReviewSubscriberProps) {
  useWebsocketSubscription({
    channel: ADMISSION_CHANNEL,
    params: { admission_id: admissionId },
    onReceived,
    enabled: true,
  });

  return null;
}

type AdmissionJobSubscriberProps = {
  admissions: Admission[];
};

export default function AdmissionJobSubscriber({ admissions }: AdmissionJobSubscriberProps) {
  const invalidateQueries = useInvalidateQueries();
  const handleInvalidate = () => invalidateQueries({ admissions: true });

  const activeJobIds = useMemo(() => {
    const ids: string[] = [];
    for (const admission of admissions) {
      if (admission.summaryJobForCurrentAttachments?.isProcessing) {
        ids.push(admission.summaryJobForCurrentAttachments.id);
      }
      if (admission.criteriaEvaluationJob?.isProcessing) {
        ids.push(admission.criteriaEvaluationJob.id);
      }
      if (admission.patientDemographicsJob?.isProcessing) {
        ids.push(admission.patientDemographicsJob.id);
      }
      if (admission.attributeExtractionJob?.isProcessing) {
        ids.push(admission.attributeExtractionJob.id);
      }
    }
    return ids;
  }, [admissions]);

  const reviewSubscriptionIds = admissions
    .filter(
      (a) => !a.data.patient?.name && !a.patientDemographicsJob?.isCompleted && !a.attributeExtractionJob?.isCompleted
    )
    .map((a) => a.id);

  return (
    <>
      {activeJobIds.map((id) => (
        <SingleJobSubscriber key={id} jobId={id} onComplete={handleInvalidate} />
      ))}
      {reviewSubscriptionIds.map((id) => (
        <ReviewSubscriber key={id} admissionId={id} onReceived={handleInvalidate} />
      ))}
    </>
  );
}
